import { Deserializer } from "jsonapi-serializer";
import { INPUT_NO_ELEMENT_SYSNAME } from "../constants/element-type";
import { genericError } from "../models/GenericError";
import { ListQuery } from "../models/ListQuery";
import { Name } from "../models/Name";
import { PaginationModel } from "../models/Pagination";
import PresetCustomField, { PresetCustomFieldWrapper } from "../models/PresetCustomField";
import { usedByStatus } from "../utilities/UsedByStatus";
import { APIConnector } from "./api/APIConnector";
import { createPagination } from "./utilities/paginationBuilder";
import { getDeviceSettingStatus } from "./utilities/StatusBuilder";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";
import { getConstraintViolation } from "./utilities/ServiceError";
import { Translator } from "../contexts/translator";

export const apiConnector = new APIConnector();

const compileFilters = (profileId, listParameters) => {
    const profileFilter = {
        key: 'profile.id',
        label: 'Profile', // not displayed
        options: [{ label: profileId, value: profileId }],
        selected: [{ label: profileId, value: profileId }]
    };
    const actionSettingTypeFilter = {
        key: 'settingType.stSysname',
        label: 'Setting Type', // not displayed
        options: [{ label: 'I_*', value: 'I_*' }],
        selected: [{ label: 'I_*', value: 'I_*' }]
    };
    return getFilterClause(
        [...listParameters?.filters ?? [], profileFilter, actionSettingTypeFilter],
        listParameters?.searchTerm
    );
}

export const getPresetInputsByProfileId = async (profileId: string, translate: Translator, listParameters?: ListQuery): Promise<PresetCustomFieldWrapper> => {
    const filter: string = compileFilters(profileId, listParameters);
    const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
    const url = `setting?include=profile,settingType,settingDatas.element${paginationString}`;

    const response = await apiConnector.fetchData(url);
    const json = await response.json();
    const pagination = createPagination(json.meta);

    return new Promise((resolve, reject) => {
        if (json.errors) {
            return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
        } else {
            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });
                }
                const presetInputs: PresetCustomField[] = data.map(preset => {
                    return {
                        id: preset['id'],
                        name: preset['trn-name'],
                        type: preset['setting-type']?.['trn-name'],
                        typeId: preset['setting-type']?.['id'],
                        usedBy: usedByStatus(preset['s-dist'], translate),
                        sdist: preset['s-dist'],
                        status: getDeviceSettingStatus(preset['s-dist'] ? preset['s-dist'] : 0),
                        profileId: preset['profile']?.['id'],
                        settingIdToCopy: preset['setting-id-to-copy'],
                        isDefault: preset['is-default'],
                        inputNo: getInputNo(preset['setting-datas'])
                    }
                })
                return resolve({ rows: presetInputs, pagination });
            });
        }
    });
}

export const getPresetInputNamesWithPagination = async (listParameters?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    const filters: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
    const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filters);
    const url = `setting?include=profile,settingType${paginationString}`;

    const response = await apiConnector.fetchData(url);
    const json = await response.json();
    const pagination = createPagination(json.meta);

    return new Promise((resolve, reject) => {
        try {
            new Deserializer().deserialize(json, (error: any, response: any[]) => {
                if (error) {
                    return reject(error);
                }

                const presetInputs: Name[] = response.map(setting => {
                    return {
                        value: setting['id'],
                        label: setting['trn-name'],
                        sdist: setting['s-dist'],
                        profileId: setting['profile']['id'],
                        settingIdToCopy: setting['id'],
                        columnId: setting['setting-type']['id'],
                    };
                })

                return resolve({ rows: presetInputs, pagination });
            });
        }
        catch (exception) {
            return reject(exception);
        }
    });
}


const getInputNo = (settingsDatas: any[]) => {
    if (settingsDatas.length === 1) {
        return settingsDatas[0]['sd-valstring'];
    }
    else if (settingsDatas.length > 1) {
        const settingDataWithInputNo = settingsDatas.find(settingData => settingData['element']['e-sysname'] === INPUT_NO_ELEMENT_SYSNAME);
        return settingDataWithInputNo['sd-valstring'];
    }

    return "";    
}